import React, { useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Divider, FormControl, InputLabel, Tab, Tabs, TextField, Typography } from "@mui/material";
import DropDown from "./dropdown";
import AudioRecordComponent from "./audioRecordComponent";
import DispositionInput from "../../interfaces/disposition_input";
import InteractionValue from "../../interfaces/interactionValue";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import Disposition from "../../interfaces/disposition";
import AudioProcessingRecord from "../../interfaces/audioProcessingRecord";
import apiClient from "../../axiosClient";
import { commentTextPattern, textPattern } from "../../constants/patterns";
import { getOrDefaultDispositionValue } from "../../utils/commonUtils";
import { v4 as uuidV4 } from 'uuid';
import Case from "../../interfaces/case";
import BackdropComponent from "./backdrop";
import AIAudioLoader from "../aidisposition/aiAudioLoader";
import { DISPOSITION_INPUT_TYPES } from "../../constants/constants";
import PermissionManagement from "../../permissions/permissionManagement";
import AIDispositionToggle from "../aidisposition/aiDispositionToggle";

interface DispositionSelectorProps {
	selectedRow: Case;
	onDispositionSubmitSuccess: () => Promise<void>;
	prefilledDisposition: Record<string, any> | null;
	setAudioProcessingRecord: React.Dispatch<React.SetStateAction<AudioProcessingRecord | null>>;
	callLogId?: string | null;
};

const DispositionSelector: React.FC<DispositionSelectorProps> = ({
	selectedRow, onDispositionSubmitSuccess, prefilledDisposition, setAudioProcessingRecord, callLogId
}) => {
	const isAIDispositionEnabled = useSelector((state: RootState) => PermissionManagement.isAIDispositionEnabled(state));
	const [dispositions, setDispositions] = useState<Disposition[][]>([]); // Stores dispositions for all levels
	const [selectedDispositions, setSelectedDispositions] = useState<string[]>([]); // Tracks user's selections
	const [dispositionLoading, setDispositionLoading] = useState<boolean>(false);
	const [latestSelectedDisposition, setLatestSelectedDisposition] = useState<string>(); // Holds latest disposition id.
	const [interactionType, setInteractionType] = useState<string>('');
	const [comment, setComment] = useState('');
	const [dispositionInputs, setDispositionInputs] = useState<DispositionInput[]>([]);
	const [dispositionValue, setDispositionValue] = useState<InteractionValue[]>([]);
	const [isDispositionAIAssisted, setIsDispositionAIAssisted] = useState<boolean>(false);
	const [audioFile, setAudioFile] = useState<File | null>(null);
	const [files, setFiles] = useState<Record<string, FileList | null>>({});
	const [latitude, setLatitude] = useState<string | null>(' ');
	const [longitude, setLongitude] = useState<string | null>(' ');
	const [loading, setLoading] = useState<boolean>(false);
	const [aiProcessing, setAiProcessing] = useState<boolean>(false);
	const [currentAudioRecord, setCurrentAudioRecord] = useState<AudioProcessingRecord | null>(null);
	const prefetchCalledCount = useRef<number>(0);

	const resetDispositions = () => {
		setDispositions(dispositions.slice(0, 1));
		setDispositionInputs([]);
		setDispositionValue([]);
		setComment('');
		setSelectedDispositions([]);
		setLatestSelectedDisposition('');
		setInteractionType('');
		setCurrentAudioRecord(null);
		prefetchCalledCount.current = 0;
	};


	useEffect(() => {
		if ('geolocation' in navigator) {
			navigator.geolocation.getCurrentPosition(
				(position) => {
					setLatitude(String(position.coords.latitude));
					setLongitude(String(position.coords.longitude));
				},
				(error) => {
					alert('Unable to retrieve your location');
				}
			);
		} else {
			alert('Geolocation is not supported by your browser');
		}
	}, []);

	const fetchDispositions = async (parentId: string) => {
		try {
			const response = await apiClient.get(`/api/v1/disposition?parent_id=${parentId}&interaction_mode=Call&client_name=${selectedRow.client_name}`);
			if (response.status === 200) {
				return response.data;
			} else {
				console.error("Failed to fetch dispositions:", response.statusText);
				return [];
			}
		} catch (error) {
			console.error("Error fetching dispositions:", error);
			return [];
		}
	};

	const fetchDispositionInput = async (disposition_id: string) => {
		try {
			const response = await apiClient.get(`/api/v1/disposition_input?disposition_id=${disposition_id}`);
			if (response.status === 200) {
				return await response.data; // Assuming the API returns a list of dispositions
			} else {
				console.error("Failed to fetch disposition required inputs:", response.statusText);
				return [];
			}
		} catch (error) {
			console.error("Error fetching disposition required inputs:", error);
			return [];
		}
	};

	const populateDispositions = async (index: number, dispositionsArray: Disposition[][], prefetchedDispositionOrder: string[]) => {
		const firstDispositionNameIndex = dispositionsArray[index]?.findIndex(val => val.disposition_name === prefetchedDispositionOrder[index]);
		const firstDispositionName = dispositionsArray[index][firstDispositionNameIndex].disposition_name;
		prefetchCalledCount.current = index + 1;
		await handleSelectDisposition(firstDispositionName, index);
	};

	/* This function fills the dispositions and disposition inputs, one at a time, based on the value of prefetchedCalledCount. We increment
	the value of this count variable by one everytime a disposition gets filled, so that we reach the leaf node of the disposition tree in 
	the correct manner, and then fill the disposition inputs. The comment is filled at the start */
	const setPrefetchedDispositions = async (
		dispositionOrder: string | undefined, commentString: string, dispositionValues: InteractionValue[] | undefined,
		dispositionsArray: Disposition[][], dispositionInputsArray: DispositionInput[]
	) => {
		if (dispositionOrder) {
			if (!comment) {
				setComment(commentString);
			}
			const prefetchedDispositionOrder = dispositionOrder?.split('-');
			const index = prefetchCalledCount.current
			if (dispositionsArray.length === index + 1) {
				await populateDispositions(index, dispositionsArray, prefetchedDispositionOrder);
			} else if (index > 1 && index < prefetchedDispositionOrder.length + 1 && dispositionInputsArray.length > 0) {
				const prefetchedValues: InteractionValue[] = dispositionValues || [];
				prefetchCalledCount.current = index + 1;
				const values: InteractionValue[] = prefetchedValues.reduce<InteractionValue[]>((acc, { field_name, field_value }) => {
					const inputDef = dispositionInputsArray.find(val => val.field_name === field_name);
					if (inputDef) {
						acc.push({ field_name, field_value, is_mandatory: inputDef.is_mandatory });
					}
					return acc;
				}, []);
				setDispositionValue(values);
			}
		}
	};

	/* This useEffect triggers the setPrefetchedDispositions function when we get the AI based disposition so that the dispositions and
	disposition inputs get filled one by one, along with the comments. audioProcessingRecord represents the AI-based disposition order */
	useEffect(() => {
		if (prefilledDisposition && Object.keys(prefilledDisposition).length > 0 && dispositions.length > 0
			&& prefetchCalledCount.current < prefilledDisposition["disposition_order"]?.split('-')?.length + 1) {
			setPrefetchedDispositions(prefilledDisposition["disposition_order"], prefilledDisposition["comment"],
				prefilledDisposition["disposition_inputs"], dispositions, dispositionInputs);
		}
	}, [prefilledDisposition, dispositions, dispositionInputs]);

	useEffect(() => {
		if (prefilledDisposition && dispositionValue && prefilledDisposition["auto_submit"]
			&& prefetchCalledCount.current > prefilledDisposition["disposition_order"]?.split('-')?.length) {
			handleSubmit();
		}
	}, [prefilledDisposition, dispositionValue]);

	useEffect(() => {
		resetDispositions();
		loadRootDispositions();
	}, [selectedRow.client_name]);

	// Load the top-level dispositions where parent_id = -1
	const loadRootDispositions = async () => {
		setDispositionLoading(true);
		const rootDispositions = await fetchDispositions('-1');
		setDispositions([rootDispositions]); // Initialize with root dispositions
		setDispositionLoading(false);
	};

	const addDispositionValue = (dispositionInput: DispositionInput, value: any) => {
		const currentDispositionValues: InteractionValue[] = dispositionValue.filter((value: InteractionValue) => value.field_name !== dispositionInput.field_name);

		// Trim the value to remove leading/trailing whitespace
		const trimmedValue = typeof value === 'string' ? value.trim() : value;

		// Always add the field to the array, even if empty, for validation purposes
		currentDispositionValues.push({
			field_name: dispositionInput.field_name,
			field_value: trimmedValue || "",
			is_mandatory: dispositionInput.is_mandatory,
		});
		setDispositionValue(currentDispositionValues);
	}


	// Handle selection of a disposition
	const handleSelectDisposition = async (selectedDispositionName: string, level: number) => {
		setDispositionLoading(true);
		const selectedDisposition: Disposition | undefined = dispositions[level].find((disposition: Disposition) => disposition.disposition_name === selectedDispositionName);
		if (!selectedDisposition) {
			alert("Invalid Disposition");
			return;
		}
		const updatedSelections = selectedDispositions.slice(0, level);
		updatedSelections[level] = selectedDisposition.disposition_name; // Update the selection for the current level
		setSelectedDispositions(updatedSelections);
		setLatestSelectedDisposition(selectedDisposition.disposition_id);
		setInteractionType(selectedDisposition.interaction_type);
		setDispositionInputs([]);
		setDispositionValue([]);

		// Fetch child dispositions
		const childDispositions = await fetchDispositions(selectedDisposition.disposition_id);

		if (childDispositions.length > 0) {
			// Update the dispositions list to include the next level
			const updatedDispositions = dispositions.slice(0, level + 1);
			updatedDispositions[level + 1] = childDispositions;
			setDispositions(updatedDispositions);
		} else {
			// No more child dispositions
			setDispositions(dispositions.slice(0, level + 1));

			const dispositionInputTypes = await fetchDispositionInput(selectedDisposition.disposition_id);
			setDispositionInputs(dispositionInputTypes);
		}
		setDispositionLoading(false);
	};

	const handleFileChange = (fileList: FileList | null, fieldName: string) => {
		let updatedFiles = { ...files };
		updatedFiles[fieldName] = fileList;
		setFiles(updatedFiles);
	};

	useEffect(() => {
		setDispositionValue(dispositionInputs.map((dispositionInput) => {
			return {
				field_name: dispositionInput.field_name,
				field_value: getOrDefaultDispositionValue(prefilledDisposition, dispositionInput),
				is_mandatory: dispositionInput.is_mandatory
			}
		}));
	}, [dispositionInputs])

	const uploadFile = async (fileList: FileList | null): Promise<string[]> => {
		if (!fileList) {
			return [];
		}

		const formData = new FormData();
		for (let i = 0; i < fileList.length; i++) {
			formData.append('file', fileList[i]);
		}

		try {
			// Replace with your actual API endpoint
			const response = await apiClient.post("/api/v1/interaction/upload_interaction_files/" + selectedRow.case_id, formData, {
				headers: {
					'Content-Type': 'multipart/form-data',
				},
				responseType: 'json'
			});
			if (response.status === 200) {
				return response.data;
			} else {
				return [];
			}
		} catch (error: any) {
			if (error.response) {
				alert('Error response data:' + error.response.data.message);
			} else if (error.request) {
				alert('No response received:' + error.request);
			} else {
				alert('Error message:' + error.message);
			}
			return [];
		}
	};

	const sendAudio = async () => {
		try {
			if (!audioFile) {
				return;
			}
			
			setAiProcessing(true);
			
			let formData = new FormData();
			formData.append("audio_file", audioFile);
			formData.append("client_name", selectedRow.client_name);
			formData.append("client_reference_id", selectedRow.client_reference_id);
			formData.append("interaction_mode", "call");
			
			const response = await apiClient.post('/api/v1/audio/upload', formData);
			if (response.status === 200) {
				const audioRecord: AudioProcessingRecord = response.data;
				setAudioProcessingRecord(audioRecord);

				const prefilledData = {
					disposition_order: audioRecord.disposition_order,
					disposition_inputs: audioRecord.disposition_inputs,
					comment: audioRecord.summarized_comment,
					auto_submit: false
				};

				resetDispositions();
				loadRootDispositions();

				// Set the audio record AFTER reset so it doesn't get cleared
				setCurrentAudioRecord(audioRecord);

				// Set the AI values immediately, no delay needed
				setTimeout(() => {
					setAudioProcessingRecord(prefilledData as any);
				}, 500); 

				setAiProcessing(false);
			}
		} catch (error) {
			setAiProcessing(false);
			alert('Error processing audio: ' + error);
		}
	};


	const handleSubmit = async () => {
		try {
			setLoading(true);
			if (dispositions.length > selectedDispositions.length) {
				throw new Error("Please select all dispositions");
			}

			if (!comment) {
				throw new Error("Please enter comments");
			}

			// Submit validation for all mandatory fields including dates 
			dispositionValue.forEach(item => {
				if (item.is_mandatory) {
					// Check if field is empty, null, or just whitespace
					if (!item.field_value || item.field_value.trim() === "") {
						throw new Error(`${item.field_name} is required and cannot be empty`);
					}

					// Special handling for audio fields
					if (item.field_value === JSON.stringify({ type: DISPOSITION_INPUT_TYPES.audio, value: "" }) && !audioFile) {
						throw new Error(`${item.field_name} is required`);
					}

					// Special handling for file based fields
					if (item.field_value === JSON.stringify({ type: DISPOSITION_INPUT_TYPES.file, value: "" }) && !files[item.field_name]) {
						throw new Error(`${item.field_name} is required`);
					}
				}
			});

			let finalDispositions = [...dispositionValue];
			for await (const [fieldName, fileList] of Object.entries(files)) {
				if (fileList && fileList.length > 0) {
					const uploadedFiles = await uploadFile(fileList);
					if (uploadedFiles.length > 0) {
						const fileValue = JSON.stringify({ type: DISPOSITION_INPUT_TYPES.file, value: uploadedFiles.join(',') });
						const existingIndex = finalDispositions.findIndex(val => val.field_name === fieldName);
						if (existingIndex !== -1) {
							finalDispositions[existingIndex].field_value = fileValue;
						}
					}
				}
			}

			let formData = new FormData();

			if (audioFile) {
				formData.append("audio", audioFile);
			}

			finalDispositions = finalDispositions.filter(item => {
				// This filters out the objects of type audio or file for whom disposition input is not mandatory
				// and no file/audio is selected for disposition submission
				return !(!audioFile && item.field_value === JSON.stringify({ type: DISPOSITION_INPUT_TYPES.audio, value: "" }))
					&& item.field_value !== JSON.stringify({ type: DISPOSITION_INPUT_TYPES.file, value: "" });
			});

			const ptpDispositionObject = finalDispositions.find(val => val.field_name === 'ptp_date');
			const followUpDispositionObject = finalDispositions.find(val => val.field_name === 'followup_date');

			// Build sources array - include either AI disposition or call_log if available
			const sources: Array<{ source_type: string; source_id: string }> = [];
			if (currentAudioRecord) {
				sources.push({
					source_type: "ai_disposition",
					source_id: currentAudioRecord.audio_processing_id
				});
			}
			if (callLogId) {
				sources.push({
					source_type: "call_log",
					source_id: callLogId
				});
			}

			let body: { [key: string]: any } = {
				client_name: selectedRow.client_name,
				agency_name: selectedRow.agency_name,
				case_id: selectedRow.case_id,
				client_reference_id: selectedRow.client_reference_id,
				disposition_id: latestSelectedDisposition,
				disposition_order: selectedDispositions.join('-'),
				interaction_mode: "Call",
				interaction_type: interactionType,
				image_url: "",
				latitude: latitude,
				longitude: longitude,
				comment: comment,
				ptp_date: ptpDispositionObject?.field_value,
				followup_date: followUpDispositionObject?.field_value,
				interaction: finalDispositions.filter(val => !['ptp_date', 'followup_date'].includes(val.field_name)).map(dispositionValue => ({
					field_name: dispositionValue.field_name,
					field_value: dispositionValue.field_value,
				})),
				sources: sources.length > 0 ? sources : []
			};

			formData.append("metadata", JSON.stringify(body));

			const response = await apiClient.post('/api/v1/interaction', formData, {
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			});
			if (response.status === 200) {
				alert("Disposition update successful");
				await onDispositionSubmitSuccess();
				resetDispositions();
			} else {
				alert("Disposition update failed");
			}
		} catch (error: any) {
			if (error.response) {
				alert('Error response data:' + error.response.data.message);
			} else if (error.request) {
				alert('No response received:' + error.request);
			} else {
				alert('Error message:' + error.message);
			}
		} finally {
			setLoading(false);
		}
	};

	return (
		<Box>
			<Tabs value={0} sx={{ marginTop: "4px" }}>
				<Tab label="Add Disposition" style={{ minWidth: "100%", fontWeight: "bolder", fontSize: "18px" }} />
			</Tabs>
			<AIDispositionToggle 
				isEnabled={isAIDispositionEnabled}
				isChecked={isDispositionAIAssisted}
				onChange={setIsDispositionAIAssisted}
			/>
			{!isDispositionAIAssisted || prefilledDisposition ? (
				<>
					{dispositionLoading ? (
						<Box
							sx={{
								width: "100%",
								height: "100%",
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							<CircularProgress color="primary" />
						</Box>
					) : (
						<>
							<Box>
								<h3>Disposition Selector</h3>
								{dispositions.map((levelDispositions, level) => (
									<DropDown
										key={level}
										label={`Level ${level} Disposition`}
										keyProp='disposition_id'
										valueProp='disposition_name'
										displayProp='disposition_label'
										selectedValue={selectedDispositions[level] || ""}
										onSelection={(value) => handleSelectDisposition(value, level)}
										items={levelDispositions}
										style={{ mb: 2 }}
									/>
								))}
							</Box>

							{dispositionInputs.map((dispositionInput: DispositionInput, index: number) => {
								if (dispositionInput.input_type === DISPOSITION_INPUT_TYPES.number) {
									return (
										<Box key={`number-${index}`}>
											<TextField
												required={dispositionInput.is_mandatory}
												margin='normal'
												label={dispositionInput.label}
												type="number"
												value={dispositionValue.find((value: InteractionValue) => value.field_name === dispositionInput.field_name)?.field_value}
												onChange={(event) => addDispositionValue(dispositionInput, event.target.value)}
												fullWidth
											/>
										</Box>
									);
								} else if (dispositionInput.input_type === DISPOSITION_INPUT_TYPES.text) {
									return (
										<Box key={`text-${index}`}>
											<TextField
												required={dispositionInput.is_mandatory}
												margin='normal'
												label={dispositionInput.label}
												value={dispositionValue.find((value: InteractionValue) => value.field_name === dispositionInput.field_name)?.field_value}
												onChange={(event) => addDispositionValue(dispositionInput, event.target.value.replace(textPattern, ""))}
												fullWidth
											/>
										</Box>
									);
								} else if (dispositionInput.input_type === DISPOSITION_INPUT_TYPES.date) {
									return (
										<Box key={`date-${index}`}>
											<FormControl fullWidth margin="normal">
												<InputLabel shrink htmlFor="ptp-date" sx={{ top: '-8px' }}>{dispositionInput.label}</InputLabel>
												<TextField
													type="date"
													required={dispositionInput.is_mandatory}
													value={dispositionValue.find((value: InteractionValue) => value.field_name === dispositionInput.field_name)?.field_value}
													onChange={(event) => addDispositionValue(dispositionInput, event.target.value)}
													fullWidth
													slotProps={{
														htmlInput: {
															min: dispositionInput.config.min === undefined ? undefined : new Date(Date.now() - (Number(dispositionInput.config.min) * 86400000)).toISOString().split("T")[0],
															max: dispositionInput.config.max === undefined ? undefined : new Date(Date.now() + (Number(dispositionInput.config.max) * 86400000)).toISOString().split("T")[0],
														}
													}}
												/>
											</FormControl>
										</Box>
									);
								} else if (dispositionInput.input_type === DISPOSITION_INPUT_TYPES.file) {
									return (
										<Box key={`file-upload-${index}`} mt={2} mb={2}>
											<Typography variant="body2" color="textSecondary">
												{dispositionInput.label}
											</Typography>

											<Box mt={1} mb={2}>
												<TextField
													type="file"
													fullWidth
													required={dispositionInput.is_mandatory}
													slotProps={{
														htmlInput: { accept: '.png,.jpeg,.pdf', multiple: true }
													}}
													onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFileChange(
														e.target.files, dispositionInput.field_name
													)}
													sx={{
														'& input': {
															padding: '8px',
														},
														borderRadius: '4px',
														border: '1px solid #ccc',
													}}
												/>
											</Box>
										</Box>
									);
								} else if (dispositionInput.input_type === DISPOSITION_INPUT_TYPES.dropdown) {
									const items = dispositionInput.config?.items?.split(';') || [];
									const currentValue = dispositionValue.find((value: InteractionValue) => value.field_name === dispositionInput.field_name)?.field_value;
									
									return (
										<DropDown
											key={`dropdown-${dispositionInput.field_name}-${currentValue}`}
											required={dispositionInput.is_mandatory}
											label={dispositionInput.label}
											selectedValue={currentValue}
											onSelection={(value) => addDispositionValue(dispositionInput, value)}
											items={items}
										/>
									);
								}
              })}
						</>
					)}

					<Box>
						<TextField
							margin='normal'
							type="text"
							label="Comment"
							value={comment}
							onChange={(event) => setComment(event?.target.value.replace(commentTextPattern, ""))}
							fullWidth
							multiline
							rows={3}
							slotProps={{
                                htmlInput: {
                                    maxLength: 1000,
                                },
                            }}
						/>
					</Box>
					<Box mt={3} mb={3} textAlign="center">
						<Button disabled={!interactionType} variant="contained" color="primary" fullWidth onClick={handleSubmit}>Submit</Button>
					</Box>
					<Divider sx={{ mb: 3 }} />
				</>
			) : isDispositionAIAssisted ? (
				<AudioRecordComponent
					label="AI Assisted Audio Disposition"
					audioFile={audioFile}
					setAudioFile={setAudioFile}
					processFunction={sendAudio}
					limitInMS={30000}
				/>
			) : null}
			<AIAudioLoader visible={aiProcessing} />
			<BackdropComponent visible={loading} />
		</Box>
	);
};

export default DispositionSelector;
